/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */
import './LinkRpsAccountsCard.scss';

import React, { useState, Fragment } from 'react';
import Content from 'services/Content';
import { contentConverter } from 'services/Content';
import TermsAndConditionsContent from './TermsAndConditionsContent';
import UnlinkConfirmationModal from './UnlinkConfirmationModal';
import ConsentModal from 'components/shared/Modal/ConsentModal';
import ActionButton from 'components/shared/Buttons/ActionButton';
import { NON_BREAKING_SPACE } from 'services/Formats';
import { unlinkRpsSubscription, linkRpsSubscription } from 'services/Rest';
import { isError } from 'support/RequestSupport';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import SaveLoadingButtonWithPermissions from 'components/shared/LoadingButton/SaveLoadingButtonWithPermissions';
import { AAMBeaconControl } from 'components/shared/Beacon';

const ActionButtonWithPermissions = WithUserPermissions(ActionButton);

const CONNECTION_ERROR = 'CONNECTION_ERROR';
const LINK_ERROR = 'LINK_ERROR';
const UNLINK_ERROR = 'UNLINK_ERROR';

const getBody = (text) => {
  const contentHtml = contentConverter.makeHtml(text);
  return <span dangerouslySetInnerHTML={{ __html: contentHtml }} />;
};

const getErrorBody = (errorType) => {
  if (errorType === CONNECTION_ERROR) {
    return (
      <div className="link-rps-accounts-text connection-error">
        Sorry, the information cannot be shown at this time. Please try to
        {NON_BREAKING_SPACE}
        <SaveLoadingButtonWithPermissions
          className="rps-connection-error-refresh-button"
          onClick={() => window.location.reload()}
          requestStatusKey="pageReload"
          shouldDisable>
          refresh
        </SaveLoadingButtonWithPermissions>{' '}
        the page or contact customer service if the problem persists.
      </div>
    );
  } else if (errorType === LINK_ERROR) {
    return (
      <div className="link-rps-accounts-text link-error">
        Sorry, the information cannot be shown at this time. Please try to
        {NON_BREAKING_SPACE}
        <SaveLoadingButtonWithPermissions
          className="rps-link-error-refresh-button"
          onClick={() => linkRpsSubscription()}
          requestStatusKey="linkRpsSubscription"
          shouldDisable>
          refresh
        </SaveLoadingButtonWithPermissions>{' '}
        the page or contact customer service if the problem persists.
      </div>
    );
  } else if (errorType === UNLINK_ERROR) {
    return (
      <div className="link-rps-accounts-text unlink-error">
        Sorry, the information cannot be shown at this time. Please try to
        {NON_BREAKING_SPACE}
        <SaveLoadingButtonWithPermissions
          className="rps-unlink-error-refresh-button"
          onClick={() => unlinkRpsSubscription()}
          requestStatusKey="unlinkRpsSubscription"
          shouldDisable>
          refresh
        </SaveLoadingButtonWithPermissions>{' '}
        the page or contact customer service if the problem persists.
      </div>
    );
  }
  return null;
};

const LinkRpsAccountsHasError = ({ errorType }) => (
  <Fragment>
    <div className="link-rps-accounts-container">
      <p className="link-rps-accounts-header">
        Workplace Retirement Plan Account(s)
      </p>
      {getErrorBody(errorType)}
    </div>
  </Fragment>
);

const LinkRpsAccountsCard = ({ subscribed }) => {
  const hasLinkSubscriptionError = isError('linkRpsSubscription');
  const hasUnlinkSubscriptionError = isError('unlinkRpsSubscription');

  const [showTermsAndConditions, setShowTermsAndConditions] = useState(false);
  const [showUnlinkConfirmation, setShowUnlinkConfirmation] = useState(false);

  const handleCancel = () => setShowTermsAndConditions(false);
  const handleKeep = () => setShowUnlinkConfirmation(false);

  const handleAgree = () => {
    setShowTermsAndConditions(false);
    linkRpsSubscription();
  };

  const handleUnlink = () => {
    setShowUnlinkConfirmation(false);
    unlinkRpsSubscription();
  };

  return !hasLinkSubscriptionError && !hasUnlinkSubscriptionError ? (
    <Fragment>
      {showTermsAndConditions && (
        <ConsentModal
          close={() => setShowTermsAndConditions(false)}
          containerClassName="link-rps-accounts-consent-modal"
          hideAnchor={true}
          hideClose={true}
          isOpen={showTermsAndConditions}
          label="link-rps-accounts-consent-modal"
          title=" "
          disableClickOff>
          <TermsAndConditionsContent
            onCancel={handleCancel}
            onAgree={handleAgree}
          />
        </ConsentModal>
      )}
      {showUnlinkConfirmation && (
        <UnlinkConfirmationModal onKeep={handleKeep} onUnlink={handleUnlink} />
      )}
      <div className="link-rps-accounts-container">
        <p className="link-rps-accounts-header">
          Workplace Retirement Plan Account(s)
        </p>
        <div className="link-rps-accounts-text">
          {subscribed
            ? getBody(Content.rpsSubscribed)
            : getBody(Content.rpsNotSubscribed)}
        </div>
        {!subscribed ? (
          <ActionButtonWithPermissions
            className="link-rps-accounts-button"
            id="link-rps-accounts-button"
            onClick={() => setShowTermsAndConditions(true)}
            type="primary"
            shouldDisable>
            Link Retirement Plan Account(s)
          </ActionButtonWithPermissions>
        ) : (
          <div className="beacon-on-light">
            <AAMBeaconControl inline toggle-switch>
              <label htmlFor ={'rsp-opt'} slot="label">
                <span className="toggle-text" aria-hidden="true">
                  On
                </span>
              </label>
              <input
                type="checkbox"
                aria-label={'rsp-opt'}
                name="rsp-opt"
                id={'rsp-opt'}
                checked={subscribed}
                onChange={() => setShowUnlinkConfirmation(true)}
              />
            </AAMBeaconControl>
            </div>
        )}
      </div>
    </Fragment>
  ) : (
    <LinkRpsAccountsHasError
      errorType={hasLinkSubscriptionError ? LINK_ERROR : UNLINK_ERROR}
    />
  );
};

LinkRpsAccountsCard.defaultProps = {
  subscribed: false
};

export { LinkRpsAccountsCard, LinkRpsAccountsHasError };
